import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, TextField, Autocomplete } from '@mui/material';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import PageTitle from '../components/PageTitle';
import * as endPoints from '../assets/js/endpoints';
import { useNotification } from '../assets/contexts/notificationContext';
import { useConfirm } from '../assets/contexts/confirmContext';
import Loader from '../components/loader';
import DataTable from '../components/DataTable';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ActivityAttendanceChart from './activityAttendanceChart';
import { Helmet } from 'react-helmet';


function ManageActivities() {
  const [activities, setActivities] = useState([]);
  const [selected, setSelected] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [clients, setClients] = useState([]);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [registerForm, setRegisterForm] = useState({
    client_id: '',
    name: '',
    last_name: '',
    phone: '',
    email: '',
    attendees: 1,
    notes: ''
  });
  const [editRegOpen, setEditRegOpen] = useState(false);
  const [editRegForm, setEditRegForm] = useState({
    id: '',
    name: '',
    last_name: '',
    phone: '',
    email: '',
    attendees: 1,
    notes: ''
  });
  const [editActivityOpen, setEditActivityOpen] = useState(false);
  const [activityForm, setActivityForm] = useState({
    id: '',
    service_name: '',
    date: '',
    time: '',
    max_attendees: ''
  });
  const { showNotification } = useNotification();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(false);


  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [acts, clis] = await Promise.all([
          endPoints.getActivitiesAdmin(),
          endPoints.getAllClients()
        ]);
        setActivities(Array.isArray(acts) ? acts : []);
        setClients(Array.isArray(clis) ? clis : clis?.data || []);
      } catch (_e) {
        } finally {
        setLoading(false);        

      }
    };
    load();
  }, []);

  const openRegs = async id => {
    try {
      const data = await endPoints.getActivityRegistrations(id);
      setSelected(data);
      setRegistrations(data.registrations || []);
    } catch (_e) {
    }
  };

  const closeDialog = () => {
    setSelected(null);
    setRegistrations([]);
  };

  const columns = [
    { id: 'service_name', label: 'Nombre' },
    { id: 'date', label: 'Fecha' },
    { id: 'time', label: 'Inicio' },
    { id: 'registered', label: 'Registrados' },
    {id: 'inscripciones', label: 'Inscripciones',}
  ];

  const regColumns = [
    { id: 'name', label: 'Nombre' },
    { id: 'email', label: 'Email' },
    { id: 'phone', label: 'Teléfono' },
    { id: 'attendees', label: 'Asistentes' },
  ];

  const rows = activities.map(a => ({
    id: a.id,
    service_name: a.service_name || a.service?.name,
    date: a.date,
    time: (a.time || '').substring(0, 5),
    registered: `${a.registered_attendees || 0} / ${a.max_attendees || '-'}`,
    inscripciones: (
    <Button variant="contained" size="small" onClick={() => openRegs(a.id)} sx={{ mr:1 }}>
      Ver inscripciones
    </Button>
    )
  }));


  const regRows = registrations.map(r => ({
    id: r.id,
    name: `${r.name} ${r.last_name}`,
    email: r.email,
    phone: r.phone,
    attendees: r.attendees
  }));

  const handleDeleteRegistration = async regId => {
    const ok = await confirm('¿Eliminar inscripción?');
    if (!ok) return;
    try {
      await endPoints.deleteActivityRegistration(selected.id, regId);
      const reg = registrations.find(r => r.id === regId);
      setRegistrations(r => r.filter(x => x.id !== regId));
      if (reg) {
        setActivities(a => a.map(x => x.id === selected.id ? { ...x, registered_attendees: (x.registered_attendees || 0) - reg.attendees } : x));
      }
      showNotification('Inscripción eliminada', 'success');
    } catch (_e) {
      showNotification('Error eliminando inscripción', 'error');
    }
  };

  const registrationActions = row => (
    <>
      <Button size="small" onClick={() => openEditRegistration(row.id)} sx={{ mr:1 }}>
        <EditIcon />
      </Button>
      <Button size="small" color="error" onClick={() => handleDeleteRegistration(row.id)}>
        <DeleteIcon />
      </Button>
    </>
  );


  const handleDelete = async id => {
    const ok = await confirm('¿Eliminar actividad?');
    if (!ok) return;
    try {
      await endPoints.deleteAppointment(id);
      setActivities(a => a.filter(x => x.id !== id));
      showNotification('Actividad eliminada', 'success');
    } catch (_e) {
      showNotification('Error eliminando actividad', 'error');
    }
  };

  const activityActions = row => (
    <>
      <Button size="small" onClick={() => openEditActivity(row.id)} sx={{ mr:1 }}>
        <EditIcon />
      </Button>
      <Button size="small" color="error" onClick={() => handleDelete(row.id)}>
        <DeleteIcon />
      </Button>
    </>
  );

  const openRegister = () => {
    setRegisterForm({
      client_id: '',
      name: '',
      last_name: '',
      phone: '',
      email: '',
      attendees: 1,
      notes: ''
    });
    setRegisterOpen(true);
  };

  const closeRegister = () => setRegisterOpen(false);

  const handleRegisterChange = e => {
    const { name, value } = e.target;
    setRegisterForm(f => ({ ...f, [name]: value }));
  };

  const handleClientSelect = client => {
    const id = client?.id || '';
    if (client) {
      setRegisterForm(f => ({
        ...f,
        client_id: id,
        name: client.name || '',
        last_name: client.apellidos || '',
        phone: client.telefono || '',
        email: client.email || ''
      }));
    } else {
      setRegisterForm(f => ({ ...f, client_id: '', name: '', last_name: '', phone: '', email: '' }));
    }
  };

  const handleRegisterSubmit = async e => {
    e.preventDefault();
    const { name, last_name, phone, email, attendees, notes } = registerForm;
    try {
      const payload = { name, last_name, phone, attendees, notes };
      if (email) payload.email = email;
      const reg = await endPoints.registerActivity(selected.id, payload);
      setRegistrations(r => [...r, reg]);
      setActivities(a => a.map(x => x.id === selected.id ? { ...x, registered_attendees: (x.registered_attendees || 0) + parseInt(attendees) } : x));
      showNotification('Inscripción registrada', 'success');
      closeRegister();
    } catch (err) {
      const msg = err.response?.data?.error || 'Error registrando';
      showNotification(msg, 'error');
    }
  };

  const openEditRegistration = id => {
    const reg = registrations.find(r => r.id === id);
    if (!reg) return;
    setEditRegForm({
      id: reg.id,
      name: reg.name,
      last_name: reg.last_name,
      phone: reg.phone,
      email: reg.email || '',
      attendees: reg.attendees,
      notes: reg.notes || ''
    });
    setEditRegOpen(true);
  };

  const closeEditRegistration = () => setEditRegOpen(false);

  const handleEditRegChange = e => {
    const { name, value } = e.target;
    setEditRegForm(f => ({ ...f, [name]: value }));
  };

  const handleEditRegSubmit = async e => {
    e.preventDefault();
    const { id, name, last_name, phone, email, attendees, notes } = editRegForm;
    try {
      const payload = { name, last_name, phone, attendees, notes };
      if (email) payload.email = email;
      const updated = await endPoints.updateActivityRegistration(selected.id, id, payload);
      const old = registrations.find(r => r.id === id);
      setRegistrations(r => r.map(x => x.id === id ? updated : x));
      if (old) {
        const diff = parseInt(attendees) - old.attendees;
        if (diff !== 0) {
          setActivities(a => a.map(act => act.id === selected.id ? { ...act, registered_attendees: (act.registered_attendees || 0) + diff } : act));
        }
      }
      showNotification('Inscripción actualizada', 'success');
      closeEditRegistration();
    } catch (_err) {
      showNotification('Error actualizando inscripción', 'error');
    }
  };

  const openEditActivity = id => {
    const act = activities.find(a => a.id === id);
    if (!act) return;
    setActivityForm({
      id: act.id,
      service_name: act.service_name || act.service?.name || '',
      date: act.date || '',
      time: (act.time || '').substring(0, 5),
      max_attendees: act.max_attendees || ''
    });
    setEditActivityOpen(true);
  };

  const closeEditActivity = () => setEditActivityOpen(false);

  const handleActivityChange = e => {
    const { name, value } = e.target;
    setActivityForm(f => ({ ...f, [name]: value }));
  };

  const handleActivitySubmit = async e => {
    e.preventDefault();
    const { id, service_name, date, time, max_attendees } = activityForm;
    try {
      const data = new FormData();
      data.append('service_name', service_name);
      data.append('date', date);
      data.append('time', time);
      if (max_attendees !== '') data.append('max_attendees', max_attendees);
      await endPoints.updateAppointment(id, data);
      setActivities(a => a.map(x => x.id === id ? { ...x, service_name, date, time, max_attendees } : x));
      showNotification('Actividad actualizada', 'success');
      closeEditActivity();
    } catch (_err) {
      showNotification('Error actualizando actividad', 'error');
    }
  };

    if (loading) return <Loader />;

    

  return (
    <>
      <Helmet>
        <title>Gestión de Actividades | CES</title>
      </Helmet>
      <DashboardLayout>
        <Box py={3}>
          <PageTitle>Actividades</PageTitle>
          {/* <ActivityAttendanceChart /> */}
          {loading ? (
            <Loader />
          ) : (
            <DataTable columns={columns} rows={rows} actions={activityActions} />
          )}
          <Dialog open={Boolean(selected)} onClose={closeDialog} fullWidth maxWidth="md">
            <DialogTitle>Inscripciones</DialogTitle>
            <DialogContent>
              {registrations.length > 0 ? (
                <DataTable columns={regColumns} rows={regRows} actions={registrationActions} />
              ) : (
                <Typography>No hay inscripciones.</Typography>
              )}
            </DialogContent>
            <DialogActions sx={{display: 'flex', justifyContent: 'center', p:2, gap:2 }}>

              <Button variant="contained" onClick={openRegister}>Añadir inscripción</Button>
              <Button variant="contained" onClick={closeDialog}>Cerrar</Button>
            </DialogActions>
          </Dialog>

          <Dialog open={registerOpen} onClose={closeRegister} fullWidth maxWidth="sm">
            <DialogTitle>Registrar cliente</DialogTitle>
            <DialogContent>
              <Box component="form" onSubmit={handleRegisterSubmit} sx={{ display:'flex', flexDirection:'column', gap:2, mt:1 }}>
                <Autocomplete
                  options={clients}
                  value={clients.find(c => String(c.id) === String(registerForm.client_id)) || null}
                  onChange={(_event, newValue) => handleClientSelect(newValue)}
                  getOptionLabel={option => `${option.name} ${option.apellidos}`}
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                  renderInput={params => (
                    <TextField {...params} label="Cliente" placeholder="Cliente no registrado" />
                  )}
                />
                <TextField label='Nombre' name='name' value={registerForm.name} onChange={handleRegisterChange} required />
                <TextField label='Apellidos' name='last_name' value={registerForm.last_name} onChange={handleRegisterChange} required />
                <TextField label='Teléfono' name='phone' value={registerForm.phone} onChange={handleRegisterChange} required />
                <TextField label='Email' name='email' value={registerForm.email} onChange={handleRegisterChange} />
                <TextField label='Número de asistentes' type='number' name='attendees' value={registerForm.attendees} onChange={handleRegisterChange} required />
                <TextField label='Notas' name='notes' value={registerForm.notes} onChange={handleRegisterChange} multiline />
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, alignSelf: 'center' }}>
              <Button onClick={closeRegister}>Cancelar</Button>
              <Button variant="contained" onClick={handleRegisterSubmit}>Guardar</Button>

            </DialogActions>
          </Dialog>

          <Dialog open={editRegOpen} onClose={closeEditRegistration} fullWidth maxWidth="sm">
            <DialogTitle>Editar inscripción</DialogTitle>
            <DialogContent>
              <Box component="form" onSubmit={handleEditRegSubmit} sx={{ display:'flex', flexDirection:'column', gap:2, mt:1 }}>
                <TextField label='Nombre' name='name' value={editRegForm.name} onChange={handleEditRegChange} required />
                <TextField label='Apellidos' name='last_name' value={editRegForm.last_name} onChange={handleEditRegChange} required />
                <TextField label='Teléfono' name='phone' value={editRegForm.phone} onChange={handleEditRegChange} required />
                <TextField label='Email' name='email' value={editRegForm.email} onChange={handleEditRegChange} />
                <TextField label='Número de asistentes' type='number' name='attendees' value={editRegForm.attendees} onChange={handleEditRegChange} required />
                <TextField label='Notas' name='notes' value={editRegForm.notes} onChange={handleEditRegChange} multiline />
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, alignSelf: 'center' }}>
              <Button onClick={closeEditRegistration}>Cancelar</Button>
              <Button variant="contained" onClick={handleEditRegSubmit}>Guardar</Button>
            </DialogActions>
          </Dialog>

          <Dialog open={editActivityOpen} onClose={closeEditActivity} fullWidth maxWidth="sm">
            <DialogTitle>Editar actividad</DialogTitle>
            <DialogContent>
              <Box component="form" onSubmit={handleActivitySubmit} sx={{ display:'flex', flexDirection:'column', gap:2, mt:1 }}>
                <TextField label='Servicio' name='service_name' value={activityForm.service_name} onChange={handleActivityChange} required />
                <TextField label='Fecha' type='date' name='date' value={activityForm.date} onChange={handleActivityChange} InputLabelProps={{ shrink: true }} required />
                <TextField label='Hora' type='time' name='time' value={activityForm.time} onChange={handleActivityChange} InputLabelProps={{ shrink: true }} required />
                <TextField label='Cupo máximo' type='number' name='max_attendees' value={activityForm.max_attendees} onChange={handleActivityChange} />
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, alignSelf: 'center' }}>
              <Button onClick={closeEditActivity}>Cancelar</Button>
              <Button variant="contained" onClick={handleActivitySubmit}>Guardar</Button>
            </DialogActions>
          </Dialog>
        </Box>
      </DashboardLayout>
    </>
  );
}

export default ManageActivities;
